// BUG-518: the discount code as the basket and checkout see it. The code itself lives in the cart
// store (kept with the basket, cleared with it after a paid order); what the API said about it for
// the current lines lives here, shared by every component that shows it, so the drawer opened over
// checkout never asks the API a second time for the same basket.

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { create } from 'zustand'
import { useCartStore } from '../../store/cartStore'
import { validateDiscountCode } from './shopApi'
import {
  discountRefusalOf,
  isWellFormedCode,
  linesSignature,
  normaliseCode,
  requestLines,
  type DiscountRefusal,
  type DiscountValidation,
} from './discountCode'
import { countsAsFailedAttempt, lockedSecondsOf, useDiscountLock } from './discountLock'
import { track } from '../analytics/track'

/** What the API said about a code for one basket. `failed` = no answer (network, rate limit). */
export type DiscountOutcome =
  | { kind: 'applied'; validation: DiscountValidation }
  | { kind: 'refused'; refusal: DiscountRefusal }
  | { kind: 'failed' }

/**
 * `none`: no code. `idle`: a code but an empty basket (nothing to check). `checking`: asking the API.
 */
export type DiscountState = { kind: 'none' } | { kind: 'idle' } | { kind: 'checking' } | DiscountOutcome

/** A basket change waits this long before the code is checked again, so a run of + clicks asks once. */
export const RECHECK_DELAY_MS = 300

/** The answers kept for this page load; dropped wholesale past this many (a long session). */
const MAX_RESULTS = 50

interface ResultsState {
  results: Record<string, DiscountOutcome>
  record: (key: string, outcome: DiscountOutcome) => void
  forget: (key: string) => void
}

export const useDiscountResults = create<ResultsState>()((set) => ({
  results: {},
  record: (key, outcome) =>
    set((s) => ({ results: { ...(Object.keys(s.results).length >= MAX_RESULTS ? {} : s.results), [key]: outcome } })),
  forget: (key) =>
    set((s) => {
      const rest = { ...s.results }
      delete rest[key]
      return { results: rest }
    }),
}))

const inFlight = new Set<string>()

// BUG-527: `discount_apply` reports the answer to the shopper's Apply once, not every re-check after
// a basket change. Only an applied code is sent as `coupon`: a refused one can be any typed text.
let reportFor: string | null = null

function reportOutcome(code: string, result: 'applied' | 'invalid' | 'locked'): void {
  if (reportFor !== code) return
  reportFor = null
  track('discount_apply', result === 'applied' ? { result, coupon: code } : { result })
}

export function _resetDiscountReportForTesting(): void {
  reportFor = null
}

async function check(key: string, body: Parameters<typeof validateDiscountCode>[0]): Promise<void> {
  if (inFlight.has(key)) return
  inFlight.add(key)
  let outcome: DiscountOutcome | null
  let locked: number | null = null
  try {
    outcome = { kind: 'applied', validation: await validateDiscountCode(body) }
  } catch (err) {
    const refusal = discountRefusalOf(err)
    // BUG-519: count the failure here too, so this page never sends the seventh, and take the
    // server's lock when it names one. A 429 lock leaves no answer: the code is asked again once it lifts.
    if (refusal && countsAsFailedAttempt(refusal.code)) useDiscountLock.getState().fail()
    locked = lockedSecondsOf(err)
    if (locked !== null) useDiscountLock.getState().lockFor(locked)
    outcome = refusal ? { kind: 'refused', refusal } : locked !== null ? null : { kind: 'failed' }
  }
  inFlight.delete(key)
  if (outcome) useDiscountResults.getState().record(key, outcome)
  if (outcome?.kind === 'applied') reportOutcome(body.code, 'applied')
  else if (outcome?.kind === 'refused') reportOutcome(body.code, 'invalid')
  else if (locked !== null) reportOutcome(body.code, 'locked')
}

export interface Discount {
  /** The code as kept ('' for none), upper case. */
  code: string
  state: DiscountState
  /** The API's figures while the code applies to these lines; null otherwise. */
  applied: DiscountValidation | null
  /** Keeps the typed code and checks it. False when nothing was typed. */
  apply: (raw: string) => boolean
  remove: () => void
  /** The order API refused the code after all (used up meanwhile): show that, take it off the totals. */
  markRefused: (refusal: DiscountRefusal) => void
}

export function useDiscount(
  lines: ReadonlyArray<{ productId: string; variantId: string | null; quantity: number }>,
  locale: string,
  delayMs = RECHECK_DELAY_MS,
): Discount {
  const code = useCartStore((s) => s.discountCode)
  const setCode = useCartStore((s) => s.setDiscountCode)
  const signature = linesSignature(requestLines(lines))
  // The request lines are rebuilt only when the basket really changed, not on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const body = useMemo(() => requestLines(lines), [signature])
  const key = `${code}|${locale}|${signature}`
  const outcome = useDiscountResults((s) => s.results[key])
  const lockedUntil = useDiscountLock((s) => s.lock.lockedUntil)
  // The basket of the last check; null until then, so a code restored on load is checked at once.
  const lastSignature = useRef<string | null>(null)

  useEffect(() => {
    if (!code || body.length === 0 || outcome) return
    if (!isWellFormedCode(code)) {
      useDiscountResults.getState().record(key, { kind: 'refused', refusal: { code: 'DISCOUNT_CODE_UNKNOWN', minimumOrderAmount: null } })
      reportOutcome(code, 'invalid')
      return
    }
    // BUG-519: a code refused for itself (not the basket's size) stays refused when only the basket
    // changed; asking again would only count as one more failed attempt.
    const prefix = `${code}|${locale}|`
    const known = Object.entries(useDiscountResults.getState().results).find(
      ([k, o]) => k.startsWith(prefix) && o.kind === 'refused' && countsAsFailedAttempt(o.refusal.code),
    )
    if (known) {
      useDiscountResults.getState().record(key, known[1])
      reportOutcome(code, 'invalid')
      return
    }
    // A code just applied is checked at once; a basket change waits for the shopper to stop clicking;
    // while the caller is locked out of codes (BUG-519) nothing is sent until the lock lifts.
    const settle = lastSignature.current !== null && lastSignature.current !== signature ? delayMs : 0
    const wait = Math.max(settle, lockedUntil - Date.now())
    lastSignature.current = signature
    const timer = setTimeout(() => void check(key, { code, lines: body, locale }), wait)
    return () => clearTimeout(timer)
  }, [code, body, locale, key, outcome, delayMs, signature, lockedUntil])

  const apply = useCallback(
    (raw: string) => {
      const next = normaliseCode(raw)
      if (!next) return false
      // The same code again after no answer: ask once more.
      const again = `${next}|${locale}|${signature}`
      if (useDiscountResults.getState().results[again]?.kind === 'failed') useDiscountResults.getState().forget(again)
      reportFor = next
      // Already answered for this basket (the same code again after Remove): report that answer now.
      const known = useDiscountResults.getState().results[again]
      if (known) reportOutcome(next, known.kind === 'applied' ? 'applied' : 'invalid')
      setCode(next)
      return true
    },
    [locale, signature, setCode],
  )

  const remove = useCallback(() => {
    if (code) track('discount_remove')
    setCode('')
  }, [code, setCode])

  const markRefused = useCallback(
    (refusal: DiscountRefusal) => useDiscountResults.getState().record(key, { kind: 'refused', refusal }),
    [key],
  )

  const state: DiscountState = !code ? { kind: 'none' } : body.length === 0 ? { kind: 'idle' } : outcome ?? { kind: 'checking' }
  return { code, state, applied: state.kind === 'applied' ? state.validation : null, apply, remove, markRefused }
}
